import { useEffect, useRef, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, MessageSquarePlus, ArrowLeft, CheckCircle2, ImagePlus, X } from "lucide-react";

/**
 * Shared support chat panel used by both merchant admin and super admin.
 * The `scope` prop switches endpoints:
 *   - merchant: /api/merchant/support/...
 *   - admin:    /api/platform/support/...
 * Polling: 5s (only when a thread is open).
 */
export default function SupportChat({ scope = "merchant" }) {
  const isAdmin = scope === "admin";
  const base = isAdmin ? "/platform/support" : "/merchant/support";

  const [threads, setThreads] = useState([]);
  const [current, setCurrent] = useState(null); // {thread, messages}
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [msg, setMsg] = useState("");
  const [filter, setFilter] = useState("all");
  const [draftImages, setDraftImages] = useState([]);
  const [msgImages, setMsgImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);

  const uploadImage = async (file, setter) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Choose an image"); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (!isAdmin) fd.append("kind", "support");
    setUploading(true);
    try {
      const url = isAdmin ? "/platform/support/upload" : "/merchant/media/upload";
      const { data } = await api.post(url, fd, { timeout: 120000 });
      if (data?.url) setter((prev) => [...prev, data.url].slice(0, 8));
    } catch (e) { toast.error(extractErr(e)); }
    finally { setUploading(false); }
  };

  const loadThreads = async () => {
    try {
      const q = isAdmin && filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`${base}/threads${q}`);
      setThreads(data);
    } catch (e) { /* ignore */ }
  };

  const openThread = async (tid) => {
    try {
      const { data } = await api.get(`${base}/threads/${tid}/messages`);
      setCurrent(data);
      await loadThreads(); // refresh unread counts
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }), 50);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const createThread = async () => {
    if (!draft.subject.trim() || (!draft.body.trim() && draftImages.length === 0)) { toast.error("Subject and a message or image are required"); return; }
    try {
      const { data } = await api.post(`${base}/threads`, { ...draft, image_urls: draftImages });
      setDraft({ subject: "", body: "" });
      setDraftImages([]);
      setShowNew(false);
      await loadThreads();
      await openThread(data.id);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const send = async () => {
    if ((!msg.trim() && msgImages.length === 0) || !current) return;
    const body = msg;
    const images = msgImages;
    setMsg("");
    setMsgImages([]);
    try {
      await api.post(`${base}/threads/${current.thread.id}/messages`, { body, image_urls: images });
      await openThread(current.thread.id);
    } catch (e) { toast.error(extractErr(e)); setMsg(body); setMsgImages(images); }
  };

  const closeThread = async () => {
    if (!current || !isAdmin) return;
    try { await api.patch(`${base}/threads/${current.thread.id}/status`, { status: current.thread.status === "closed" ? "open" : "closed" }); await openThread(current.thread.id); await loadThreads(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  // Poll threads every 5s (and messages when a thread is open)
  useEffect(() => { loadThreads(); }, [filter]); // eslint-disable-line
  useEffect(() => {
    const id = setInterval(() => {
      loadThreads();
      if (current) {
        api.get(`${base}/threads/${current.thread.id}/messages`).then(({ data }) => {
          if (data.messages.length !== current.messages.length) {
            setCurrent(data);
            setTimeout(() => scrollRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }), 50);
          }
        }).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(id);
  }, [current, filter]); // eslint-disable-line

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-160px)] min-h-[520px]">
      {/* Threads list */}
      <Card className="p-0 border-slate-200/70 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2">
          <div>
            <div className="font-semibold text-slate-900">{isAdmin ? "All conversations" : "Support"}</div>
            <div className="text-xs text-slate-500">{isAdmin ? "Across all merchants" : "Chat with the MeraStartup team"}</div>
          </div>
          {!isAdmin && <Button onClick={() => setShowNew(true)} data-testid="support-new" size="sm" className="bg-orange-500 hover:bg-orange-600 text-slate-900"><MessageSquarePlus className="w-4 h-4 mr-1" />New</Button>}
        </div>
        {isAdmin && (
          <div className="px-3 py-2 border-b border-slate-100 flex gap-1 text-xs">
            {["all", "open", "closed"].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`support-filter-${s}`} className={`px-2.5 py-1 rounded-full capitalize ${filter === s ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{s}</button>
            ))}
          </div>
        )}
        <div className="overflow-y-auto flex-1">
          {threads.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">No conversations yet.</div>}
          {threads.map((t) => {
            const unread = isAdmin ? t.unread_admin : t.unread_merchant;
            const active = current?.thread?.id === t.id;
            return (
              <button key={t.id} onClick={() => openThread(t.id)} data-testid={`support-thread-${t.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-slate-900 truncate">{t.subject}</div>
                  {unread > 0 && <span className="shrink-0 min-w-[20px] h-5 rounded-full bg-orange-500 text-slate-900 text-xs font-bold flex items-center justify-center px-1.5">{unread}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                  {isAdmin && <span className="font-mono text-orange-700">{t.tenant_slug}</span>}
                  {isAdmin && <span>·</span>}
                  <span className={t.status === "closed" ? "text-slate-500" : "text-emerald-700"}>{t.status}</span>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Chat */}
      <Card className="p-0 border-slate-200/70 overflow-hidden flex flex-col">
        {showNew && !isAdmin ? (
          <div className="p-6 flex flex-col gap-3 h-full">
            <button onClick={() => setShowNew(false)} data-testid="support-new-cancel" className="self-start inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div>
              <div className="font-semibold text-slate-900 text-lg">Start a new conversation</div>
              <div className="text-sm text-slate-500 mt-1">Our team will reply here — usually within a few hours during business days.</div>
            </div>
            <div><label className="text-sm text-slate-700">Subject</label><Input data-testid="support-new-subject" className="mt-1" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="Trouble with checkout" /></div>
            <div className="flex-1 flex flex-col"><label className="text-sm text-slate-700">How can we help?</label><Textarea data-testid="support-new-body" className="mt-1 flex-1 resize-none" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Explain what's happening — include order numbers if any." /></div>
            <div className="flex flex-wrap gap-2">
              {draftImages.map((url) => (
                <div key={url} className="relative w-16 h-16">
                  <img src={mediaUrl(url)} alt="" className="w-16 h-16 object-cover rounded-md border border-slate-200" />
                  <button type="button" className="absolute -top-1 -right-1 bg-white rounded-full border border-slate-200 p-0.5" onClick={() => setDraftImages((xs) => xs.filter((u) => u !== url))}><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="w-16 h-16 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-slate-500 cursor-pointer" data-testid="support-new-image">
                <ImagePlus className="w-5 h-5" />
                <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadImage(f, setDraftImages); }} />
              </label>
            </div>
            <Button onClick={createThread} data-testid="support-new-send" disabled={uploading} className="self-start bg-orange-500 hover:bg-orange-600 text-slate-900"><Send className="w-4 h-4 mr-1" /> Send</Button>
          </div>
        ) : !current ? (
          <div className="flex items-center justify-center h-full text-sm text-slate-400 p-6 text-center">
            {threads.length ? "Pick a conversation on the left" : (isAdmin ? "No open conversations right now." : "Start a new conversation with our support team.")}
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{current.thread.subject}</div>
                <div className="text-xs text-slate-500">
                  {isAdmin && <span className="font-mono text-orange-700">{current.thread.tenant_slug || current.thread.tenant_id}</span>}
                  {isAdmin && " · "}
                  <span className={current.thread.status === "closed" ? "text-slate-500" : "text-emerald-700"}>{current.thread.status}</span>
                </div>
              </div>
              {isAdmin && <Button variant="outline" size="sm" onClick={closeThread} data-testid="support-close-toggle"><CheckCircle2 className="w-4 h-4 mr-1" /> {current.thread.status === "closed" ? "Reopen" : "Close"}</Button>}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
              {current.messages.map((m) => {
                const mine = isAdmin ? m.sender_kind === "admin" : m.sender_kind === "merchant";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-slate-900 text-slate-100 rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                      <div className="text-[11px] font-medium uppercase tracking-wide opacity-70 mb-0.5">{m.sender_kind === "admin" ? (m.sender_name || "Support") : (m.sender_name || "You")}</div>
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
                      {(m.image_urls || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {m.image_urls.map((url) => (
                            <a key={url} href={mediaUrl(url)} target="_blank" rel="noreferrer">
                              <img src={mediaUrl(url)} alt="" className="max-w-[180px] max-h-36 rounded-lg object-cover" data-testid="support-msg-image" />
                            </a>
                          ))}
                        </div>
                      )}
                      <div className="text-[10px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-slate-200 space-y-2">
              {msgImages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {msgImages.map((url) => (
                    <div key={url} className="relative w-14 h-14">
                      <img src={mediaUrl(url)} alt="" className="w-14 h-14 object-cover rounded-md border border-slate-200" />
                      <button type="button" className="absolute -top-1 -right-1 bg-white rounded-full border border-slate-200 p-0.5" onClick={() => setMsgImages((xs) => xs.filter((u) => u !== url))}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <label className="shrink-0 h-11 w-11 rounded-md border border-slate-200 flex items-center justify-center text-slate-600 cursor-pointer hover:bg-slate-50" data-testid="support-attach-image">
                  <ImagePlus className="w-5 h-5" />
                  <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadImage(f, setMsgImages); }} />
                </label>
                <Textarea data-testid="support-msg" placeholder="Write a reply…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} className="min-h-[44px] max-h-32 resize-none" />
                <Button onClick={send} data-testid="support-send" disabled={uploading || (!msg.trim() && msgImages.length === 0)} className="bg-orange-500 hover:bg-orange-600 text-slate-900"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
